import { createColumnHelper } from '@tanstack/react-table';
import {
  AddPriceBandIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  Avatar,
  Bin,
  CalculationIcon,
  LinkButton,
  TextIcon,
  VenueIcon,
  Typography,
  EllipsisMenu,
  Plus,
  RoundingIcon,
  IOption,
  XIcon,
} from '@weareopenr/component-library';
import * as Popover from '@radix-ui/react-popover';
import styles from './PriceSchemeRulesTab.module.scss';
import { InlineNameForm } from '../InlineNameForm/InlineNameForm';
import { PriceRuleToolTip } from '../PricingTablePriceInput/PriceRuleToolTip';

import { RoundingMethod, AdjustmentType, RoundingType, IPriceRule } from 'src/types/price.types';
import { formatAdjustmentValue } from 'src/helpers/formatPrice/formatPrice';
import { ExternalSystemImages } from '../../constants';
import { PriceBandRuleSteps } from '../PriceBandRuleModal';
import {
  isDescriptiveRow,
  isEmptyRow,
  isCreateBandCtaRow,
  isInlineCreateRow,
  isChannelRow,
  TTableRow,
  TPricingCategoryRow,
  isPricingCategoryRow,
} from './types';
import { CREATE_PRICE_BAND } from '../PriceSchemeChannelRulesTab/constants';
import { pluralize } from 'src/helpers/pluralize/pluralize';
import { IPriceBandWithVenues } from 'src/types/priceBand.types';
import { TPriceBandAndRules } from '../PriceSchemeBandsRulesTab/types';
import { ChannelRuleSteps } from '../ChannelRuleModal/ChannelRuleModalContent';
import { CategoryOverrideRuleSteps } from '../CategoryOverrideRuleModal';

const InheritanceIcon = ({ inheritedRule }: { inheritedRule: IPriceRule }) => {
  const parentName =
    inheritedRule.pricing_category?.name ??
    inheritedRule.price_band?.name ??
    inheritedRule.extension_instance?.name ??
    'Unknown';

  return (
    <div className={styles['inheritance-cell']}>
      <Popover.Root>
        <Popover.Trigger aria-label="Inherited rule" className={styles['inheritance-trigger']}>
          <CalculationIcon />
        </Popover.Trigger>
        <Popover.Portal>
          <PriceRuleToolTip parentRule={inheritedRule} />
        </Popover.Portal>
      </Popover.Root>
      Inheriting rule from {parentName}
    </div>
  );
};

const renderCategoryCalculation = (row: TPricingCategoryRow) => {
  if (!row.overrideRule) {
    if (row.inheritedRule) return <InheritanceIcon inheritedRule={row.inheritedRule} />;
    return <span className={styles['no-override']}>No rule defined</span>;
  }
  const { adjustment } = row.overrideRule;
  if (adjustment.type === AdjustmentType.NONE) return '-';
  return formatAdjustmentValue(adjustment);
};

const columnHelper = createColumnHelper<TTableRow>();

const renderRounding = (rounding: { type: RoundingType; method?: RoundingMethod; value?: number }) => {
  const value = rounding.method ? `${rounding.value} (${rounding.method.toLowerCase()})` : rounding.value;
  switch (rounding.type) {
    case RoundingType.UP:
      return (
        <div className={styles.alignedRounding}>
          <ArrowUpIcon size="small" />
          {value}
        </div>
      );
    case RoundingType.DOWN:
      return (
        <div className={styles.alignedRounding}>
          <ArrowDownIcon size="small" />
          {value}
        </div>
      );
    case RoundingType.NEAREST:
      return (
        <div className={styles.nearest}>
          <ArrowUpIcon size="small" />
          <ArrowDownIcon size="small" />
          {value}
        </div>
      );
    default:
      return value;
  }
};

type TNameFormConfig = {
  onStartCreate: () => void;
  onSubmit: (name: string, row: TTableRow) => Promise<void>;
  onClose: () => void;
  existingNames: string[];
  onEditName: (priceBandId: string) => void;
  editingPriceBandId: string | null;
};

type TChannelActions = {
  handleCreate: (channelId: string) => void;
  handleEdit: (priceRuleId: string, step: ChannelRuleSteps) => void;
  handleDelete: (priceRuleId: string) => void;
};

const ChannelRowActions = ({ row, handleCreate, handleEdit, handleDelete }: { row: IPriceRule } & TChannelActions) => {
  if (row.price_rule_id.endsWith('_rule')) {
    return (
      <EllipsisMenu
        options={[
          {
            id: 'add-channel-rule',
            name: 'Add Channel Price Rule',
            onOptionClick: () => handleCreate(row.extension_instance.id),
            icon: Plus,
          },
        ]}
        variant="ghost"
        hoverStyle="high-contrast"
      />
    );
  }

  const { price_rule_id } = row;
  return (
    <EllipsisMenu
      options={[
        {
          id: 'edit-calculation',
          name: 'Edit Calculation',
          onOptionClick: () => handleEdit(price_rule_id, ChannelRuleSteps.Adjustment),
          icon: CalculationIcon,
        },
        {
          id: 'edit-rounding',
          name: 'Edit Rounding',
          onOptionClick: () => handleEdit(price_rule_id, ChannelRuleSteps.Rounding),
          icon: RoundingIcon,
        },
        { id: 'delete-divider', isDivider: true },
        {
          id: 'delete-rule',
          name: 'Delete Channel Rule',
          onOptionClick: () => handleDelete(price_rule_id),
          icon: Bin,
          hoverColour: 'tomato',
        },
      ]}
      variant="ghost"
      hoverStyle="high-contrast"
    />
  );
};

type TPriceBandActions = {
  onEditVenues: (priceBand: TPriceBandAndRules) => void;
  onDelete: (priceBand: IPriceBandWithVenues) => void;
  onEdit: (rule: IPriceRule, step: PriceBandRuleSteps) => void;
};

type TPriceCategoryActions = {
  onAddOverride: (categoryRow: TPricingCategoryRow) => void;
  handleDelete: (categoryRow: TPricingCategoryRow) => void;
  handleEdit: (categoryRow: TPricingCategoryRow, step: CategoryOverrideRuleSteps) => void;
};

type TColumnsConfig = {
  nameForm: TNameFormConfig;
  priceBandActions: TPriceBandActions;
  channelActions: TChannelActions;
  priceCategoryOverrideActions: TPriceCategoryActions;
};

export const getRulesTableColumns = ({
  nameForm: { onStartCreate, onSubmit, onClose, existingNames, onEditName, editingPriceBandId },
  priceBandActions: { onEditVenues, onDelete, onEdit },
  channelActions: { handleCreate, handleEdit, handleDelete },
  priceCategoryOverrideActions: {
    onAddOverride,
    handleDelete: handlePricingCategoryOverrideDelete,
    handleEdit: handlePricingCategoryOverrideEdit,
  },
}: TColumnsConfig) => [
  columnHelper.accessor((row) => row, {
    header: 'Rules',
    size: 450,
    cell: (props) => {
      const row = props.getValue();
      if (isDescriptiveRow(row)) {
        return (
          <span data-row-type="descriptive">
            <Typography weight="bold">{row.label}</Typography>
          </span>
        );
      }
      if (isEmptyRow(row)) {
        return row.message;
      }
      if (isCreateBandCtaRow(row)) {
        return (
          <LinkButton onClick={onStartCreate} type="button">
            {CREATE_PRICE_BAND}
          </LinkButton>
        );
      }
      if (isInlineCreateRow(row)) {
        return (
          <InlineNameForm onSubmit={(name) => onSubmit(name, row)} onClose={onClose} existingNames={existingNames} />
        );
      }
      if (isPricingCategoryRow(row)) {
        return (
          <div className={styles['cell-with-subtitle']}>
            <span>{row.category.name}</span>
            <span className={styles['cell-subtitle']}>
              {row.category.products_assigned} {pluralize('product', row.category.products_assigned)}
            </span>
          </div>
        );
      }

      const ruleName = row.extension_instance?.name ?? row.price_band?.name;
      if (row.price_band?.id && row.price_band.id === editingPriceBandId) {
        const otherNames = existingNames.filter((n) => n !== ruleName);
        return (
          <InlineNameForm
            onSubmit={(name) => onSubmit(name, row)}
            onClose={onClose}
            existingNames={otherNames}
            defaultValue={ruleName}
          />
        );
      }
      // The avatar represents the extension, so it ignores the instance half of the label.
      const avatarName = row.externalSystemName ?? ruleName;
      const initials = avatarName
        .split(' ')
        .slice(0, 2)
        .map((word) => word[0].toUpperCase())
        .join('');
      const image = ExternalSystemImages[avatarName];
      const venueSubtitle =
        row?.venueCount !== undefined ? (
          <span className={styles['cell-subtitle']}>
            {row.venueCount} {pluralize('venue', row.venueCount)}
          </span>
        ) : null;
      if (row.extension_instance) {
        return (
          <div className={styles['rule-name-container']}>
            {image ? <Avatar imageUrl={image} variant="custom" /> : <Avatar initials={initials} variant="custom" />}
            <div className={styles['cell-with-subtitle']}>
              <span>{ruleName}</span>
              {venueSubtitle}
            </div>
          </div>
        );
      }
      return (
        <div className={styles['cell-with-subtitle']}>
          <Typography weight="bold" className={ruleName === 'Default' ? styles['default-title'] : undefined}>
            {ruleName}
          </Typography>
          {venueSubtitle}
        </div>
      );
    },
  }),
  columnHelper.accessor((row) => row, {
    header: 'Calculation',
    cell: (props) => {
      const row = props.getValue();
      if (isDescriptiveRow(row) || isEmptyRow(row) || isCreateBandCtaRow(row) || isInlineCreateRow(row)) return null;
      if (isPricingCategoryRow(row)) return renderCategoryCalculation(row);
      if (row.price_band?.id === editingPriceBandId) return null;
      const adjustment = row.adjustment;
      const hasNoRulesAttached = row?.price_rule_id?.endsWith('_rule');
      if (hasNoRulesAttached) {
        return (
          <span className={styles['no-override']}>
            {row.extension_instance ? 'No channel rule defined' : 'No price band rule defined'}
          </span>
        );
      }
      if (adjustment.type === AdjustmentType.NONE) return '-';
      return formatAdjustmentValue(adjustment);
    },
  }),
  columnHelper.accessor((row) => row, {
    header: 'Rounding',
    cell: (props) => {
      const row: TTableRow = props.getValue();
      if (isDescriptiveRow(row) || isEmptyRow(row) || isCreateBandCtaRow(row) || isInlineCreateRow(row)) return null;

      if (isPricingCategoryRow(row)) {
        if (row.overrideRule) {
          const rounding = row.overrideRule.rounding;
          if (!rounding || rounding.type === RoundingType.NONE) return '-';
          return renderRounding(rounding);
        }
        return null;
      }

      if (row.price_rule_id?.endsWith('_rule')) return '';
      if (row.rounding.type === RoundingType.NONE) return '-';
      return renderRounding(row.rounding);
    },
  }),
  columnHelper.display({
    id: 'actions',
    size: 60,
    cell: (props) => {
      const row = props.row.original;
      if (isDescriptiveRow(row) || isEmptyRow(row) || isCreateBandCtaRow(row) || isInlineCreateRow(row)) return null;
      // EllipsisMenu for Pricing category rows - show add/edit override options
      if (isPricingCategoryRow(row)) {
        if (row.category.category_id === null) return null;
        const hasRule = row.overrideRule;
        const editOptions: IOption[] = [
          { id: 'edit-divider', isDivider: true },
          {
            id: 'edit-calculation',
            name: 'Edit Calculation',
            icon: CalculationIcon,
            onOptionClick: () => handlePricingCategoryOverrideEdit(row, CategoryOverrideRuleSteps.Adjustment),
          },
          {
            id: 'edit-rounding',
            name: 'Edit Rounding',
            icon: RoundingIcon,
            onOptionClick: () => handlePricingCategoryOverrideEdit(row, CategoryOverrideRuleSteps.Rounding),
          },
          { id: 'delete-divider', isDivider: true },
          {
            id: 'delete',
            name: 'Delete category rule',
            icon: XIcon,
            onOptionClick: () => handlePricingCategoryOverrideDelete(row),
            hoverColour: 'tomato',
          },
        ];
        return (
          <EllipsisMenu
            options={[
              {
                id: 'add-category-override-rule',
                name: 'Add category override rule',
                icon: AddPriceBandIcon,
                isDisabled: !!row.overrideRule,
                onOptionClick: () => onAddOverride(row),
              },
              ...(hasRule ? editOptions : []),
            ]}
            variant="ghost"
            hoverStyle="high-contrast"
          />
        );
      }

      if (isChannelRow(row)) {
        return (
          <ChannelRowActions
            row={row}
            handleCreate={handleCreate}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
          />
        );
      }

      // Price band rows
      const priceBandRow = row as TPriceBandAndRules;
      if (!onEdit || !priceBandRow.price_band?.id) return null;

      const priceBand: IPriceBandWithVenues = {
        priceBandId: priceBandRow.price_band.id,
        name: priceBandRow.price_band.name,
        venues: priceBandRow.venues ?? [],
      };

      const ruleOptions =
        priceBandRow.price_rule_id && !priceBandRow.price_rule_id.endsWith('_rule')
          ? [
              {
                id: 'edit-calculation',
                name: 'Edit Calculation',
                icon: CalculationIcon,
                onOptionClick: () => onEdit(priceBandRow, PriceBandRuleSteps.Adjustment),
              },
              {
                id: 'edit-rounding',
                name: 'Edit Rounding',
                icon: RoundingIcon,
                onOptionClick: () => onEdit(priceBandRow, PriceBandRuleSteps.Rounding),
              },
            ]
          : [
              {
                id: 'add-price-band-rule',
                name: 'Add price band rule',
                icon: Plus,
                onOptionClick: () => onEdit(priceBandRow, PriceBandRuleSteps.Adjustment),
              },
            ];

      return (
        <div className={styles['ellipsis-menu-container']}>
          <EllipsisMenu
            options={[
              {
                id: 'edit-name',
                name: 'Edit Name',
                icon: TextIcon,
                onOptionClick: () => onEditName(priceBand.priceBandId),
              },
              {
                id: 'edit-venues',
                name: 'Edit Venue Allocation',
                icon: VenueIcon,
                onOptionClick: () => onEditVenues({ ...priceBandRow, ...priceBand }),
              },
              { id: 'edit-divider', isDivider: true },
              ...ruleOptions,
              { id: 'delete-divider', isDivider: true },
              {
                id: 'delete',
                name: 'Delete Price Band',
                icon: Bin,
                onOptionClick: () => onDelete(priceBand),
                hoverColour: 'tomato',
              },
            ]}
            variant="ghost"
            hoverStyle="high-contrast"
          />
        </div>
      );
    },
  }),
];
